// src/pages/Sanitary/SanitaryReportList.js

import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Card, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaHistory,
  FaSearch,
  FaEye,
  FaEdit,
  FaPlus,
  FaDownload,
} from "react-icons/fa";

import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import AddEditSanitaryModal from "./AddEditSanitaryModal";
import SanitaryDetailsModal from "./SanitaryDetailsModal";
import SanitaryHistoryModal from "./SanitaryHistoryModal";

function SanitaryReportList() {
  // --- STATE MANAGEMENT ---
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fundList, setFundList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // --- CONFIGURATION ---
  const userType = localStorage.getItem("type");
  const API_URL =
    process.env.REACT_APP_SUPER_ADMIN_API_URL || "http://localhost/accapi";
  const token = localStorage.getItem("token");

  // --- DATA FETCHING ---
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [reportsRes, fundsRes, deptsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/list-sanitary.php`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/fundlist.php`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/departmentlist.php`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/userlist.php`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const reportsData =
        reportsRes.data?.data && Array.isArray(reportsRes.data.data)
          ? reportsRes.data.data
          : [];
      setReports(
        reportsData.sort(
          (a, b) => new Date(b.memo_date) - new Date(a.memo_date)
        )
      );

      if (fundsRes.data?.success) setFundList(fundsRes.data.funds || []);
      if (deptsRes.data?.success) {
        setDepartmentList(
          deptsRes.data.departments.filter(
            (d) => d.department_name !== userType
          ) || []
        );
      }
      // Robustly handle user list response
      const usersData =
        usersRes.data?.users ||
        (Array.isArray(usersRes.data) ? usersRes.data : []);
      setUserList(usersData);
    } catch (error) {
      toast.error("Data could not be fetched. Please check API connection.");
      console.error("Data fetching error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // --- MODAL HANDLERS ---
  const handleAddNewClick = () => {
    setIsEditMode(false);
    setSelectedReport(null);
    setShowAddEditModal(true);
  };
  const handleEditClick = (row) => {
    setIsEditMode(true);
    setSelectedReport(row);
    setShowAddEditModal(true);
  };
  const handleDetailsClick = (row) => {
    setSelectedReport(row);
    setShowDetailsModal(true);
  };
  const handleHistoryClick = (row) => {
    setSelectedReport(row);
    setShowHistoryModal(true);
  };

  // --- UTILITY FUNCTIONS ---
  const formatDate = (dateString) =>
    !dateString ? "N/A" : dateString.split(" ")[0];
  const formatAmount = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString("en-IN")}`;
  const getFundName = (id) =>
    fundList.find((f) => f.id === id)?.fund_name || "Unknown";
  const getDepartmentName = (id) =>
    departmentList.find((d) => d.id === id)?.department_name || "Unknown";
  const getUserName = (id) =>
    userList.find((u) => u.id === id)?.name || "Unknown";

  // --- FILTERING ---
  const filteredReports = useMemo(() => {
    if (!searchTerm) return reports;
    return reports.filter((report) =>
      Object.values(report).some(
        (val) =>
          val && val.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [reports, searchTerm]);

  // --- DATATABLE COLUMNS (সম্পূর্ণ এবং নিখুঁত) ---
  const columns = useMemo(
    () => [
      {
        name: "Memo No",
        selector: (row) => row.memo_no,
        sortable: true,
        width: "150px",
      },
      {
        name: "Memo Date",
        selector: (row) => formatDate(row.memo_date),
        sortable: true,
        width: "140px",
      },
      {
        name: "Name of Works",
        selector: (row) => row.name_of_works,
        sortable: true,
        grow: 2,
        minWidth: "250px",
      },
      {
        name: "Resolution Copy",
        cell: (row) =>
          row.resolution_copy ? (
            <a
              href={`${API_URL}/${row.resolution_copy}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-info"
            >
              <FaDownload />
            </a>
          ) : (
            "N/A"
          ),
        width: "150px",
        center: true,
      },
      {
        name: "Allotment Fund",
        selector: (row) => getFundName(row.alotment_head_id),
        sortable: true,
        width: "200px",
      },
      {
        name: "Allotment Amt",
        selector: (row) => formatAmount(row.alotment_amount),
        sortable: true,
        right: true,
        width: "160px",
      },
      {
        name: "Work Order No",
        selector: (row) => row.work_order_meno,
        sortable: true,
        width: "160px",
      },
      {
        name: "Engaged Agency",
        selector: (row) => row.engaged_agency,
        sortable: true,
        width: "200px",
      },
      {
        name: "Final Amount",
        selector: (row) => formatAmount(row.final_amount),
        sortable: true,
        right: true,
        width: "160px",
      },
      {
        name: "Concerned Person",
        selector: (row) => getUserName(row.name_of_concerned),
        sortable: true,
        width: "200px",
      },
      {
        name: "Forwarded To",
        selector: (row) =>
          `${getDepartmentName(row.send_to)} (${getUserName(row.user_id)})`,
        sortable: true,
        width: "250px",
      },
      {
        name: "Physical Status",
        selector: (row) => row.physical_status,
        sortable: true,
        width: "150px",
      },
      {
        name: "Final Payment",
        selector: (row) => formatAmount(row.final_payment_amount),
        sortable: true,
        right: true,
        width: "160px",
      },
      {
        name: "Unspent Balance",
        selector: (row) => formatAmount(row.unspent_balance),
        sortable: true,
        right: true,
        width: "160px",
      },
      { name: "Remarks", selector: (row) => row.remarks, width: "200px" },
      {
        name: "Status",
        sortable: true,
        selector: (row) => row.final_status,
        cell: (row) => {
          const status = (row.final_status || "pending").toLowerCase();
          const badgeColor =
            { complete: "success", pending: "warning", "payment done": "info" }[
              status
            ] || "secondary";
          return (
            <span className={`badge bg-${badgeColor} text-capitalize`}>
              {status.replace("_", " ")}
            </span>
          );
        },
        width: "150px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="d-flex flex-wrap">
            <Button
              variant="outline-info"
              size="sm"
              className="m-1"
              onClick={() => handleDetailsClick(row)}
            >
              <FaEye />
            </Button>
            <Button
              variant="outline-warning"
              size="sm"
              className="m-1"
              onClick={() => handleEditClick(row)}
            >
              <FaEdit />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="m-1"
              onClick={() => handleHistoryClick(row)}
            >
              <FaHistory />
            </Button>
          </div>
        ),
        minWidth: "150px",
        sticky: "right",
      },
    ],
    [fundList, departmentList, userList]
  );

  // --- JSX & RENDERING ---
  const customStyles = { headCells: { style: { fontWeight: "bold" } } };
  const subHeaderComponent = useMemo(
    () => (
      <Row className="g-3 w-100 my-2 align-items-center">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-md-end">
          <Button onClick={handleAddNewClick} variant="primary">
            <FaPlus className="me-2" /> Add New File
          </Button>
        </Col>
      </Row>
    ),
    [searchTerm]
  );

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <DepartmentSidebar />
        <div
          className="flex-grow-1 p-3"
          style={{
            background: "#f0f2f5",
            maxWidth: "90vw",
            overflowX: "scroll",
          }}
        >
          <Card
            className="shadow-sm"
            style={{ border: "none", borderRadius: "18px" }}
          >
            <Card.Header
              as="h5"
              style={{
                background: "#fff",
                borderBottom: "1px solid #eee",
                padding: "20px",
              }}
            >
              Sanitary Department: Sanitary Work Order Lists
            </Card.Header>
            <Card.Body style={{ padding: "0" }}>
              <DataTable
                columns={columns}
                data={filteredReports}
                pagination
                progressPending={loading}
                subHeader
                subHeaderComponent={subHeaderComponent}
                highlightOnHover
                responsive
                customStyles={customStyles}
              />
            </Card.Body>
          </Card>
        </div>
      </div>

      {showAddEditModal && (
        <AddEditSanitaryModal
          show={showAddEditModal}
          handleClose={() => setShowAddEditModal(false)}
          isEditMode={isEditMode}
          reportData={selectedReport}
          onSuccess={fetchAllData}
          fundList={fundList}
          departmentList={departmentList}
          userList={userList}
        />
      )}
      {showDetailsModal && (
        <SanitaryDetailsModal
          show={showDetailsModal}
          handleClose={() => setShowDetailsModal(false)}
          detailsRow={selectedReport}
        />
      )}
      {showHistoryModal && (
        <SanitaryHistoryModal
          show={showHistoryModal}
          handleClose={() => setShowHistoryModal(false)}
          reportId={selectedReport?.id}
          memoNo={selectedReport?.memo_no}
        />
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default SanitaryReportList;
